import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "api/api";
import endpoint from "api/endpoints";
import { delayCall } from "api/util";
import { publicItemName } from "config/config";

import { Icon, SearchField } from "@/design-system";

export default function SearchBar() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [active, setActive] = useState(false);
  const requestIdRef = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    const normalizedQuery = query.trim();
    const requestId = ++requestIdRef.current;

    if (!normalizedQuery) {
      setResults([]);
      setLoading(false);
      setHasSearched(false);
      return undefined;
    }

    setLoading(true);
    setHasSearched(false);

    return delayCall(async () => {
      try {
        const response = await api.post(
          endpoint.POST_GET_ITEMS_BY_NAME({ limit: 5, offset: 0 }),
          { name: normalizedQuery }
        );

        if (requestId === requestIdRef.current) {
          setResults(response.data || []);
          setHasSearched(true);
        }
      } catch {
        if (requestId === requestIdRef.current) {
          setResults([]);
          setHasSearched(true);
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 1000);
  }, [query]);

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setHasSearched(false);
    setActive(false);
  };

  const handleSearchAll = () => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;

    navigate(`/products/by-name/${encodeURIComponent(normalizedQuery)}`);
    clearSearch();
  };

  const handleResultClick = (item) => {
    if (item?.id == null || String(item.id).trim() === "") {
      handleSearchAll();
      return;
    }

    navigate(`/product/${String(item.id).toLowerCase()}`);
    clearSearch();
  };

  const showDropdown =
    active && Boolean(query.trim()) && (loading || hasSearched);

  return (
    <div
      className="relative w-full"
      onFocusCapture={() => setActive(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setActive(false);
      }}
    >
      <SearchField
        value={query}
        onChange={setQuery}
        onSubmit={handleSearchAll}
        placeholder="Search products, brands or SKUs"
      />

      {showDropdown && (
        <div
          className="absolute left-0 right-0 top-[calc(100%_+_8px)] z-[var(--z-modal)] overflow-hidden rounded-lg border border-line-subtle bg-card px-[6px] py-[6px] shadow-lg"
          aria-live="polite"
        >
          {loading ? (
            <div className="flex items-center justify-center gap-[var(--space-2)] px-[14px] py-[18px] text-sm text-ink-3">
              <span
                className="h-[16px] w-[16px] animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"
                aria-hidden="true"
              />
              Searching…
            </div>
          ) : results.length > 0 ? (
            <>
              {results.map((item) => {
                // Item names can carry a staff-only note after a "^"
                // ("… 300/Bx ^ 1185-C"); never show it (same rule as the PDP
                // title). The raw itemid still keys the row and the route.
                const displayName =
                  publicItemName(
                    item.displayname || item.storedisplayname || item.itemid
                  ) || "Product";
                const sku = publicItemName(item.itemid);

                return (
                  <button
                    key={`${item.id}-${sku || displayName}`}
                    type="button"
                    className="flex w-full cursor-pointer items-center gap-[var(--space-3)] rounded-md border-0 bg-transparent px-[10px] py-[9px] text-left font-sans [transition:background_var(--duration-fast)] hover:bg-soft focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                    onClick={() => handleResultClick(item)}
                  >
                    <span className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-sm border border-line-subtle bg-neutral-50 text-ink-3">
                      <Icon name="inventory_2" size={20} weight={400} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold text-ink">
                        {displayName}
                      </span>
                      {sku && sku !== displayName && (
                        <span className="block text-xs text-ink-3">{sku}</span>
                      )}
                    </span>
                    <Icon
                      name="arrow_forward"
                      size={18}
                      weight={400}
                      className="shrink-0 text-ink-3"
                    />
                  </button>
                );
              })}
              <button
                type="button"
                className="mt-[var(--space-1)] flex w-full cursor-pointer items-center justify-center gap-[var(--space-2)] border-0 border-t border-line-subtle bg-transparent px-[11px] py-[11px] font-sans text-sm font-semibold text-blue-700 hover:text-blue-800 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                onClick={handleSearchAll}
              >
                <Icon name="search" size={18} weight={400} />
                See all results for “{query.trim()}”
              </button>
            </>
          ) : (
            <div className="px-[14px] py-[18px] text-center text-sm text-ink-3">
              No matches — try a product name, brand, or SKU.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
